
import { useFactory, useLocalStorage, useTransform } from "@ispb/react-hooks"
import { flow } from "fp-ts/function"
import { GenQLPin } from "genql-pin"
import { createContext, Dispatch, SetStateAction } from "react"
import { maybe } from "shared/maybe"
import { Mop } from "../namespaces/Mop.js"
import { Ramda } from "../namespaces/Ramda.js"
import { hasuraProcQuery, PublicControlPoint, PublicExecutionAction, PublicProcedure } from "./print.js"

export const LOCKED_OUT_COLOR = "success" as const
export const ENERGIZED_COLOR = "danger" as const
export const PHOTO_COLUMN_STYLE = { width: "5%" }
export const ICON_COLUMN_STYLE = { width: "5%" }

export async function loadData(hasura: GenQLPin.Client, barCode: string) {
    const result = await hasura.query(hasuraProcQuery(barCode))
    return result.assets
}

export function getCanChangeControlPoint(controlPoint: string, index: number, allControlPoints: string[], actions: PublicExecutionAction[], executionStatus: ExecutionStatus) {
    const cpStatus = useFactory(getControlPointStatus, [controlPoint, actions])
    const first = index === 0
    const last = index + 1 === allControlPoints.length
    const previous = index > 0 ? allControlPoints.at(index - 1) : undefined
    const previousDifferent = maybe(previous, previous => cpStatus !== useFactory(getControlPointStatus, [controlPoint, actions]), () => false)
    const next = index < allControlPoints.length ? allControlPoints.at(index + 1) : undefined
    const nextDifferent = maybe(next, next => cpStatus !== useFactory(getControlPointStatus, [controlPoint, actions]), () => false)
    if (previousDifferent || nextDifferent) {
        return true
    }
    if (first) {
        if (executionStatus.allEnergized) {
            return true
        }
    }
    if (last) {
        if (executionStatus.allLockedOut) {
            return true
        }
    }
}

export function getControlPointStatus(controlPoint: string, actions: PublicExecutionAction[]) {
    return Ramda.sortBy(_ => _.createdOn, actions.filter(_ => _.controlPoint.id === controlPoint)).map(_ => _.status).at(-1) ?? false
}

export type ExecutionStatus = ReturnType<typeof getExecutionStatus>

export function getExecutionStatus(actions: PublicExecutionAction[], controlPoints: PublicControlPoint[]) {
    const allStates = controlPoints.map(_ => getControlPointStatus(_.id, actions))
    const allEnergized = allStates.filter(_ => _).length === 0
    const allLockedOut = allStates.filter(_ => !_).length === 0
    return {
        allEnergized,
        allLockedOut,
    }
}

export interface PublicProcedureSession {

    readonly hasura: GenQLPin.Client
    readonly data: PublicProcedure
    readonly setData: Dispatch<SetStateAction<PublicProcedure>>

}

export const PublicProcedureContext = createContext<PublicProcedureSession | undefined>(undefined)

export interface AnonymousName {

    readonly name: string
    readonly companyName: string

}

const ANONYMOUS_SCHEMA = Mop.maybeOrUndefined(
    flow(
        Mop.fromJsonObject(),
        Mop.object({
            name: Mop.isString(),
            companyName: Mop.isString(),
        }),
    ),
)

export function useAnonymous() {
    return useTransform(...useLocalStorage("anonymous"), flow(Mop.of<string | undefined>, ANONYMOUS_SCHEMA, Mop.failToUndefined), JSON.stringify)
}
